/**
 * Feature E02-F01-T05 — TanStack Query hook for the project list.
 *
 * The typed, domain-specific wrapper over the generic `useProjectsList` cache
 * hook (api/queries.ts → E10-F01). It pins the wire type to the shared
 * `ProjectListItemDTO[]` contract so every caller (Dashboard, ProjectsListView)
 * reads the same shape, and exposes the list invalidation the SPA needs after a
 * write — replacing the legacy manual `state.projects` + `loadProjects()`.
 *
 * 1:1 behaviour port of `public/assets/js/dashboard.js`:
 *   - `loadProjects` → `useProjects()` (GET /projects, cached);
 *   - the list reloads on navigation back to the dashboard (TanStack refetch on
 *     mount/window-focus) and is INVALIDATED after `createManualProject` and after
 *     a stage change / `update_project` (advanceStage) — `useInvalidateProjects`;
 *   - while loading, the table body shows "Loading…" (the consumer branches on
 *     `query.isLoading`).
 *
 * The invalidation targets the whole `projects` domain prefix (`qk.projects.all`),
 * so BOTH the list and any per-project detail query refetch — the new/updated
 * project can never show stale.
 */
import { useCallback } from 'react';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ProjectListItemDTO } from '@eer/shared';
import { useProjectsList } from '../api/queries.js';
import { qk, type ListParams } from '../api/queryKeys.js';
import type { ApiResultError } from '../api/client.js';

/**
 * Cached list of projects (`GET /projects`), typed to the shared
 * `ProjectListItemDTO[]` contract. The result's `error` (on failure) is an
 * `ApiResultError` carrying `detail.message`; `isLoading` drives the table's
 * "Loading…" body and `data ?? []` the empty ("No projects") state.
 *
 * @param params optional list filters (part of the cache key).
 */
export function useProjects(
  params?: ListParams,
): UseQueryResult<ProjectListItemDTO[], ApiResultError> {
  return useProjectsList<ProjectListItemDTO[]>(params);
}

/**
 * Returns a stable callback that invalidates the entire `projects` cache domain
 * (list + details), so the list refetches after a mutation. Call it from the
 * `onSuccess` of `createManualProject` and after `advanceStage` / `update_project`
 * (the legacy app re-ran `loadProjects()` at exactly those points).
 *
 * Prefer `useDomainMutation(fn, ['projects'])` (api/queries.ts) when the write is
 * a TanStack mutation — it invalidates automatically. This hook covers the
 * imperative case (a write performed outside a `useMutation`).
 */
export function useInvalidateProjects(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: qk.projects.all }),
    [queryClient],
  );
}
